/**
 * useFeatureAccess Hook
 *
 * Provides centralized access control logic for functional features that require both:
 * 1. Active user status (user.active === true) - for non-account-owners only
 * 2. Active subscription (required for ALL users including account owners)
 *
 * This hook is used to control access to:
 * - SidePanel menus
 * - HowDoI page
 * - Other core application functional features
 *
 * Note: These are functional features, not premium features.
 * Premium features will be a separate set of features built in the future.
 *
 * Access Rules:
 * - Account Owners (account_admin): Assumed always active, but MUST have active subscription
 * - Regular Users: Must be BOTH active AND have an active subscription
 * - Deactivated Regular Users: No access regardless of subscription
 * - No Subscription: No access for anyone (including account owners)
 *
 * @module useFeatureAccess
 */

import { useMemo } from "react";
import { useUser } from "../components/app/context/UserContext";
import { useSubscription } from "../components/app/context/SubscriptionContext";
import { useRoleCheck } from "./useRoleCheck";
import { useTranslation } from "../i18n/LanguageContext";

/**
 * Custom hook to determine feature access based on user status and subscription
 * @returns {Object} Feature access flags
 */
export const useFeatureAccess = () => {
  const { loggedInUser, isDemoSeat, isExtensionReview } = useUser();
  const {
    hasActiveSubscription,
    isInGracePeriod,
    hasLapsedSubscription,
    hasInitiallyLoaded,
  } = useSubscription();
  const { isAccountAdmin } = useRoleCheck(loggedInUser);
  const { t } = useTranslation();

  /**
   * Check if user is currently active (not deactivated)
   * @returns {boolean}
   */
  const isUserActive = useMemo(() => {
    if (!loggedInUser) return false;
    return loggedInUser.active === true || loggedInUser.active === 1;
  }, [loggedInUser]);

  /**
   * Check if user is still in verification flow (not yet fully verified)
   * Users in verification flow are expected to be inactive - this is normal
   * @returns {boolean}
   */
  const isInVerificationFlow = useMemo(() => {
    if (!loggedInUser) return false;
    return !loggedInUser.emailVerified || !loggedInUser.phoneVerified;
  }, [loggedInUser]);

  /**
   * Check if user was deactivated by admin (vs just being in verification flow)
   * Only true if user completed verification but is now inactive
   * @returns {boolean}
   */
  const isDeactivatedByAdmin = useMemo(() => {
    if (!loggedInUser) return false;
    // User is deactivated by admin only if they're inactive AND have completed verification
    return !isUserActive && !isInVerificationFlow;
  }, [loggedInUser, isUserActive, isInVerificationFlow]);

  /**
   * Check if user is an account owner/admin
   * Account owners have special privileges and don't require active subscription
   * @returns {boolean}
   */
  const isAccountOwner = useMemo(() => {
    return isAccountAdmin();
  }, [isAccountAdmin]);

  /**
   * Determine whether the account's subscription has lapsed (expired past the
   * grace period and is now inactive).
   *
   * This is distinct from a brand-new account that has never subscribed: a
   * never-subscribed owner has NO subscription record (subscription === null)
   * and must keep access to the Dashboard onboarding/journey so they can pick a
   * plan. A *lapsed* account, by contrast, has a subscription record that is no
   * longer active.
   *
   * Lapsed is true only when:
   * - A user is logged in and subscription state has finished loading, AND
   * - They are NOT a demo seat (demo users intentionally run without a paid
   *   subscription and must not be locked out), AND
   * - They are NOT still completing verification (new-user onboarding), AND
   * - The subscription is NOT in the grace period (grace keeps full access), AND
   * - There is no active subscription, AND
   * - For account owners / platform admins: the account has a terminal
   *   subscription on record (distinguishing a real lapse from a brand-new
   *   account that never subscribed, whose Dashboard onboarding must keep
   *   working). For regular members: any "no active subscription" state counts
   *   as lapsed, because a member only exists under an account subscription and
   *   members cannot query subscription history (the endpoint is owner-only).
   *
   * Trial subscriptions report hasActiveSubscription === true, so trial users
   * are never considered lapsed.
   *
   * @returns {boolean}
   */
  const isSubscriptionLapsed = useMemo(() => {
    if (!loggedInUser) return false;
    // Wait for subscription state to load before locking anyone out, to avoid
    // briefly flashing the lapsed notice during initial data fetch.
    if (!hasInitiallyLoaded) return false;
    if (isDemoSeat) return false;
    if (isInVerificationFlow) return false;
    if (isInGracePeriod) return false;
    if (hasActiveSubscription) return false;
    if (isAccountOwner) return hasLapsedSubscription;
    return true;
  }, [
    loggedInUser,
    hasInitiallyLoaded,
    isDemoSeat,
    isInVerificationFlow,
    isInGracePeriod,
    hasActiveSubscription,
    isAccountOwner,
    hasLapsedSubscription,
  ]);

  /**
   * Determine if user can access functional features (SidePanel, HowDoI, etc.)
   *
   * Logic:
   * - Account Owners: Assumed always active, but still need active subscription
   * - Regular Users: Must be active AND have active subscription
   *
   * @returns {boolean}
   */
  const canAccessFunctionalFeatures = useMemo(() => {
    console.log("🔐 useFeatureAccess: Calculating functional feature access", {
      loggedInUser: loggedInUser?.email,
      isUserActive,
      isAccountOwner,
      hasActiveSubscription,
    });

    // User must be logged in
    if (!loggedInUser) {
      console.log("❌ useFeatureAccess: No logged in user");
      return false;
    }

    // Chrome Web Store reviewer seats are locked to the browser extension. Hide all
    // functional web-app features (the backend ExtensionReviewAccessFilter enforces
    // the real restriction; this is UX only).
    if (isExtensionReview) {
      console.log(
        "❌ useFeatureAccess: Extension review seat - functional features hidden",
      );
      return false;
    }

    // Users in verification flow (phone not verified) cannot access functional features.
    // They can only access Dashboard with the Account Setup Progress panel.
    if (isInVerificationFlow) {
      console.log(
        "❌ useFeatureAccess: User in verification flow - phone not verified",
      );
      return false;
    }

    // Regular users must be active (account owners are assumed always active)
    if (!isAccountOwner && !isUserActive) {
      console.log("❌ useFeatureAccess: Regular user is deactivated");
      return false;
    }

    // Everyone (including account owners) needs an active subscription
    if (!hasActiveSubscription) {
      console.log(
        isAccountOwner
          ? "❌ useFeatureAccess: Account owner but no subscription - access denied"
          : "❌ useFeatureAccess: Active user but no subscription - access denied",
      );
      return false;
    }

    // Grace period remains functional: users keep access while we show UI warnings.
    if (isInGracePeriod) {
      console.log(
        "⚠️ useFeatureAccess: Subscription in grace period - functional access retained",
      );
    }

    console.log(
      isAccountOwner
        ? "✅ useFeatureAccess: Account owner with subscription - access granted"
        : "✅ useFeatureAccess: Active user with subscription - access granted",
    );
    return true;
  }, [
    loggedInUser,
    isUserActive,
    isAccountOwner,
    hasActiveSubscription,
    isExtensionReview,
    isInVerificationFlow,
    isInGracePeriod,
  ]);

  /**
   * Get the reason why access is denied (for user messaging)
   * Only shows "deactivated" message if user completed verification but was deactivated by admin
   * Users in verification flow won't see the deactivation message
   * @returns {string|null} Denial reason or null if access is granted
   */
  const getAccessDenialReason = useMemo(() => {
    if (!loggedInUser) return "Please log in to access this feature";
    // Only show deactivation message if user completed verification but was deactivated
    if (isDeactivatedByAdmin) return t("deactivated.accessDenied");
    // Users in verification flow - don't show deactivation message, they just need to complete verification
    if (isInVerificationFlow)
      return "Please complete your account verification";
    if (!hasActiveSubscription)
      return "Please subscribe to a plan to access this feature";
    return null;
  }, [
    loggedInUser,
    isDeactivatedByAdmin,
    isInVerificationFlow,
    hasActiveSubscription,
    t,
  ]);

  return {
    // Core access flag
    canAccessFunctionalFeatures,

    // Individual status flags for granular control
    isUserActive,
    isAccountOwner,
    hasActiveSubscription,
    isInVerificationFlow,
    isDeactivatedByAdmin,
    isInGracePeriod,
    isSubscriptionLapsed,
    isDemoSeat,
    isExtensionReview,

    // Helper information
    getAccessDenialReason,

    // Computed flags for specific scenarios
    // isDeactivated is true only if user was deactivated by admin (not in verification flow)
    isDeactivated: isDeactivatedByAdmin,
    needsSubscription:
      !hasActiveSubscription && (isUserActive || isAccountOwner),
  };
};

export default useFeatureAccess;
